import { Box } from '@mui/material'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import SectionContainer from '@components/SectionContainer'
import DatePicker from '@components/DatePicker'
import SettingLimitPane from '../components/SettingLimitPane'
import { DateTime } from 'luxon'
import type useUserPage from './useUserPage'
import { LoadingButton } from '@mui/lab'
import { NotesLimitLevel } from '@types'

type UseUsersPage = ReturnType<typeof useUserPage>
type UserType = NonNullable<UseUsersPage['user']>

interface SettingsAndLimitsProps {
    settingDate: UseUsersPage['settingDate']
    loadingSettings: UseUsersPage['loadingSettings']
    setSettingDate: UseUsersPage['setSettingDate']
    getUserSettings: UseUsersPage['getUserSettings']
    settings: UserType['settings']
}

const SETTING_LEVEL_NAMES: Record<'user' | 'group' | 'org', string> = {
    user: 'the student',
    group: 'their group',
    org: 'the organisation',
}

const LIMIT_LEVEL_NAMES: Record<NotesLimitLevel, string> = {
    user: 'the student',
    group: 'their group',
    org: "the organisation's default",
    fallback: 'the house default, because no level sets one',
}

function formatHours(seconds: number) {
    const hours = (seconds / 3600).toFixed(seconds % 3600 === 0 ? 0 : 1)
    return `${hours} ${hours === '1' ? 'hour' : 'hours'}`
}

/**
 * Say which level decided, because the rule and the cap are resolved
 * separately and land on different levels more often than not. Without this
 * the pane shows a rule and a cap with no clue where either came from, which
 * is what made these cases take a database session to answer.
 */
function describeDecision(settings: UserType['settings']) {
    if (!settings?.settingLevel) return null

    /**
     * An unapproved organisation is answered before any rule is looked at, so
     * it arrives with no setting attached and used to report as "no rule at
     * any level". That sentence sends someone to add a rule, which will not
     * help: nothing about this organisation is set up to order yet.
     */
    if (settings.reason === 'org-not-approved') {
        return 'This organisation is not approved to order, so no rule applies yet.'
    }

    /**
     * No rule anywhere reports as org level with nothing attached to it, and
     * saying "Rule from the organisation" there reads as the organisation
     * having decided something. Nobody did, which is the whole answer.
     */
    const hasRule = Boolean(
        settings.userSetting ?? settings.groupSetting ?? settings.orgSetting
    )

    if (!hasRule) {
        return 'No rule at any level, so this student cannot order.'
    }

    const rule = SETTING_LEVEL_NAMES[settings.settingLevel]
    const cap = settings.limitLevel
        ? LIMIT_LEVEL_NAMES[settings.limitLevel]
        : null

    const capSize = settings.limitSeconds
        ? ` (${formatHours(settings.limitSeconds)})`
        : ''

    const where = cap
        ? `Rule from ${rule}. Cap from ${cap}${capSize}.`
        : `Rule from ${rule}.`

    /**
     * Say which of the two refused. The panes show a rule and a cap side by
     * side, and working out which one stopped a student took a database
     * session more than once.
     */
    switch (settings.reason) {
        case 'limit-reached':
            return `${where} Cannot order: the allowance is used up.`
        case 'blocked-by-rule':
            return `${where} Cannot order: blocked by that rule.`
        default:
            return where
    }
}

export default function SettingsAndLimits({
    settingDate,
    loadingSettings,
    setSettingDate,
    getUserSettings,
    settings,
}: SettingsAndLimitsProps) {
    const decision = describeDecision(settings)

    return (
        <SectionContainer title="Settings and Limits">
            <Box
                sx={{
                    gap: 2,
                    justifyContent: 'center',
                    display: 'flex',
                    mb: 2,
                }}
            >
                <DatePicker
                    disabled={loadingSettings}
                    label="For Date"
                    onChange={setSettingDate}
                    maxDate={DateTime.now()}
                    value={settingDate}
                />
                <LoadingButton
                    loading={loadingSettings}
                    onClick={getUserSettings}
                    startIcon={<ManageSearchIcon />}
                    sx={{ my: 'auto' }}
                >
                    Get Settings
                </LoadingButton>
            </Box>
            <Box sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                A rule on the student beats one on their group, which beats one
                on the organisation. The cap is resolved the same way but
                separately, so it often comes from a different level.
            </Box>
            {decision && (
                <Box
                    sx={{
                        mb: 2,
                        textAlign: 'center',
                        fontWeight: 'medium',
                    }}
                >
                    {decision}
                </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <SettingLimitPane
                    setting={
                        settings?.userSetting ??
                        settings?.groupSetting ??
                        settings?.orgSetting
                    }
                    limit={
                        settings?.userLimit ??
                        settings?.groupLimit ??
                        settings?.orgDefLimit
                    }
                    usageSeconds={settings?.usage ?? 0}
                    settingLevel={settings?.settingLevel}
                    limitLevel={settings?.limitLevel}
                />
            </Box>
        </SectionContainer>
    )
}
